import React, { useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';

/**
 * Recover Past Purchases modal — local members link shadow-profile
 * UnmatchedVisitor records to their account via receipt-transaction-#
 * (Tier 1) or card-last-4 + name match (Tier 2).
 *
 * Opens from the dashboard banner (first-login affordance) and from
 * the profile page's persistent link. Same component, same flow.
 */

interface RecoveryResult {
  merged_count: number;
  total_recovered_spend: number;
  total_recovered_points: number;
  recent_visits: Array<{
    date: string | null;
    transaction_number: string;
    items_summary: string;
    total: number;
  }>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Fires after a successful submit (any merged_count, including 0) so
  // the parent can refresh state/prompt visibility.
  onCompleted: () => void;
}

const RecoverPurchasesModal: React.FC<Props> = ({ isOpen, onClose, onCompleted }) => {
  const [txnNumber, setTxnNumber] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecoveryResult | null>(null);

  if (!isOpen) return null;

  const reset = () => {
    setTxnNumber('');
    setCardLast4('');
    setError(null);
    setResult(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    const txn = txnNumber.trim();
    const card = cardLast4.trim();
    if (!txn && !card) {
      setError('Enter a transaction number from your receipt OR the last 4 of your card.');
      return;
    }
    if (card && (card.length !== 4 || !/^\d{4}$/.test(card))) {
      setError('Card last-4 must be exactly 4 digits.');
      return;
    }

    setSubmitting(true);
    try {
      const data: RecoveryResult = await bridgeClubAPI.recoverPurchases({
        transaction_number: txn || undefined,
        card_last_four: card || undefined,
      });
      setResult(data);
      // Notify parent so the dashboard banner + profile link refresh
      // their state-driven visibility. Backend has already flipped
      // recover_purchases_state='completed'.
      onCompleted();
    } catch (err: any) {
      setError(err?.message || err?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: 560,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#065F46' }}>
            Recover Past Purchases
          </h2>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', fontSize: 24, color: '#6B7280', cursor: 'pointer' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {result ? (
            // ── Result panel ──────────────────────────────────────
            result.merged_count > 0 ? (
              <div>
                <div style={{
                  background: '#ECFDF5',
                  border: '1px solid #10B981',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#065F46', marginBottom: 6 }}>
                    🎉 Found {result.merged_count} past visit{result.merged_count === 1 ? '' : 's'}!
                  </div>
                  <div style={{ color: '#065F46', fontSize: 14, lineHeight: 1.5 }}>
                    Lifetime spend: <strong>${result.total_recovered_spend.toFixed(2)}</strong>
                    <br />
                    Points added: <strong>{result.total_recovered_points}</strong>
                  </div>
                </div>

                {result.recent_visits.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                      Recent activity:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {result.recent_visits.map((v, i) => (
                        <div
                          key={`rv-${i}`}
                          style={{
                            padding: '8px 10px',
                            background: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ color: '#374151' }}>
                              {v.date || 'Unknown date'} · #{v.transaction_number}
                            </span>
                            <strong style={{ color: '#065F46' }}>${v.total.toFixed(2)}</strong>
                          </div>
                          {v.items_summary && (
                            <div style={{ color: '#6B7280', marginTop: 2, fontSize: 12 }}>
                              {v.items_summary}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleClose}
                  style={{
                    width: '100%',
                    marginTop: 20,
                    padding: 12,
                    background: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              // No-match friendly path — never angry messaging.
              <div>
                <div style={{
                  background: '#FFFBEB',
                  border: '1px solid #F59E0B',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
                    No past purchases found
                  </div>
                  <div style={{ color: '#78350F', fontSize: 14, lineHeight: 1.5 }}>
                    We couldn't find any past purchases matching what you entered.
                    That's okay! Your future purchases will start building history right away.
                    You can try again later from your profile.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => { reset(); }}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: 'white',
                      color: '#065F46',
                      border: '1px solid #10B981',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Try again
                  </button>
                  <button
                    onClick={handleClose}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )
          ) : (
            // ── Input panel ───────────────────────────────────────
            <>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.5, marginTop: 0 }}>
                We'll find your previous visits and add them to your account.
                Provide either a receipt transaction number, the last 4 of
                your card, or both — we'll try the receipt number first.
              </p>

              {/* Option 1 — receipt transaction number */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: 6, fontSize: 14 }}>
                  Option 1 — From a recent receipt
                </label>
                <input
                  type="text"
                  placeholder="Transaction number (e.g., 20260516-0004)"
                  value={txnNumber}
                  onChange={(e) => setTxnNumber(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 14,
                    border: '1px solid #D1D5DB',
                    borderRadius: 6,
                  }}
                />
                <div style={{
                  marginTop: 8,
                  padding: 10,
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}>
                  <img
                    src="/images/receipt-transaction-example.png"
                    alt="Receipt example highlighting transaction number"
                    style={{
                      maxWidth: 140,
                      height: 'auto',
                      borderRadius: 4,
                      border: '1px solid #D1D5DB',
                    }}
                    onError={(e) => {
                      // Graceful degradation if the asset isn't deployed yet.
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                    Found near the top of your receipt, right after the date.
                    Format looks like <strong>YYYYMMDD-NNNN</strong>.
                  </div>
                </div>
              </div>

              {/* Option 2 — card last-4 */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: 6, fontSize: 14 }}>
                  Option 2 — From your card
                </label>
                <input
                  type="text"
                  placeholder="Last 4 digits"
                  value={cardLast4}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCardLast4(v);
                  }}
                  maxLength={4}
                  style={{
                    width: 120,
                    padding: '10px 12px',
                    fontSize: 14,
                    border: '1px solid #D1D5DB',
                    borderRadius: 6,
                    letterSpacing: 2,
                  }}
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  We'll match the last 4 digits of the card you used + your name.
                </div>
              </div>

              {error && (
                <div style={{
                  padding: 10,
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 6,
                  color: '#991B1B',
                  fontSize: 13,
                  marginBottom: 12,
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  onClick={handleClose}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: 12,
                    background: 'white',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: submitting ? 'wait' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    flex: 2,
                    padding: 12,
                    background: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: submitting ? 'wait' : 'pointer',
                  }}
                >
                  {submitting ? 'Looking…' : 'Recover My Purchases'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoverPurchasesModal;
